import { AxiosError } from "axios";

import { api } from "@/api/client";
import type { AccessTokenResponse, UserMe } from "@/types/api";

export async function login(username: string, password: string): Promise<AccessTokenResponse> {
  const { data } = await api.post<AccessTokenResponse>("/auth/login", { username, password });
  return data;
}

/** Вход через SSO — ПОЛНАЯ навигация браузера, не XHR: бэкенд ставит
 *  cookie состояния и уводит на Zitadel, а оттуда обратно на
 *  `/login?sso=ok|sso_error=…`. Путь с `/api` — это адрес страницы, а не
 *  вызов axios-клиента. */
export const SSO_LOGIN_URL = "/api/auth/sso/login";

/** Паузы перед повторами `/refresh`, занятого параллельным обменом (другая
 *  вкладка обновляет ту же cookie): 409 `{code: "refresh_in_progress"}`.
 *  Это не отказ — через долю секунды у браузера уже новая cookie, и повтор
 *  проходит. Повторов ровно столько, сколько пауз; дальше — обычная ошибка. */
export const REFRESH_IN_PROGRESS_DELAYS_MS = [400, 400];

function isRefreshInProgress(err: unknown): boolean {
  if (!(err instanceof AxiosError) || err.response?.status !== 409) return false;
  const data = err.response.data as { code?: unknown } | undefined;
  return data?.code === "refresh_in_progress";
}

export async function refreshSession(): Promise<AccessTokenResponse> {
  for (let attempt = 0; ; attempt++) {
    try {
      const { data } = await api.post<AccessTokenResponse>("/auth/refresh");
      return data;
    } catch (err) {
      const delay = REFRESH_IN_PROGRESS_DELAYS_MS[attempt];
      if (!isRefreshInProgress(err) || delay === undefined) throw err;
      await new Promise((resolve) => window.setTimeout(resolve, delay));
    }
  }
}

export interface LogoutResponse {
  /** SSO-сессия: адрес выхода из браузерной сессии Zitadel
   *  (`end_session_endpoint`), куда уводим после очистки своей сессии.
   *  Вход по паролю — null. */
  end_session_url: string | null;
}

/** 502 `/auth/logout` — бэкенд не смог отозвать refresh у Zitadel, но кладёт
 *  в тело `end_session_url`: браузерную сессию Zitadel всё равно нужно
 *  закрыть, иначе на общем терминале следующий войдёт в чужую учётку. */
function extractEndSessionUrlFromError(err: unknown): string | null {
  if (!(err instanceof AxiosError)) return null;
  const data = err.response?.data as { end_session_url?: unknown } | undefined;
  return typeof data?.end_session_url === "string" ? data.end_session_url : null;
}

export async function logout(): Promise<LogoutResponse> {
  try {
    const { data } = await api.post<LogoutResponse>("/auth/logout");
    return data;
  } catch (err) {
    const endSessionUrl = extractEndSessionUrlFromError(err);
    if (endSessionUrl !== null) {
      console.error(
        "Не удалось отозвать refresh-токен на logout, но браузерную сессию Zitadel всё равно закрываем",
        err,
      );
      return { end_session_url: endSessionUrl };
    }
    throw err;
  }
}

export async function fetchMe(): Promise<UserMe> {
  const { data } = await api.get<UserMe>("/auth/me");
  return data;
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<AccessTokenResponse> {
  const { data } = await api.post<AccessTokenResponse>("/auth/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return data;
}
